// NumberInputFieldWithSlider.tsx
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { type FormField } from "../formFieldType";
import MyBox from "../../MyBox";
import { Minus, Plus } from "lucide-react";

export function NumberInputField({ field }: { field: FormField }) {
  const { register, setValue, control, formState } = useFormContext();
  const [isDragging, setIsDragging] = useState(false);

  const value = useWatch({
    control,
    name: field.name,
  });

  const min = field.min ?? 0;
  const max = field.max ?? 100;
  const step = field.step ?? 1;

  // Ensure default value
  useEffect(() => {
    if (value === undefined || value === null) {
      setValue(field.name, min, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [value, field.name, min, setValue]);

  const inc = () => {
    const next = Math.min(max, (Number(value) || 0) + step);
    setValue(field.name, next, { shouldDirty: true });
  };

  const dec = () => {
    const next = Math.max(min, (Number(value) || 0) - step);
    setValue(field.name, next, { shouldDirty: true });
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setValue(field.name, newValue, { shouldDirty: true });
  };

  const error = formState.errors[field.name];
  const percentage = ((Number(value) || min) - min) / (max - min) * 100;

  return (
    <MyBox topText={field.label} required={field.required}>
      <div className="space-y-4">
        {/* Label and Value Display */}
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 sm:hidden">
            {field.label}
          </span>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {value ?? min}
          </span>
        </div>

        {/* Slider (optional) */}
        {field.showSlider && (
          <div className="relative pt-2">
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={value ?? min}
              onChange={handleSliderChange}
              onMouseDown={() => setIsDragging(true)}
              onMouseUp={() => setIsDragging(false)}
              disabled={field.disabled}
              className="
                w-full h-2 rounded-lg appearance-none cursor-pointer
                bg-gray-200 dark:bg-gray-700
                focus:outline-none focus:ring-2 focus:ring-blue-500
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              style={{
                background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>{min}</span>
              <span>{max}</span>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={dec}
            disabled={field.disabled || (Number(value) || 0) <= min}
            className={`
              w-12 h-12 rounded-xl border-2
              flex items-center justify-center
              transition-all duration-200
              ${field.disabled || (Number(value) || 0) <= min
                ? "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:scale-105 active:scale-95"
              }
            `}
            aria-label="Decrease value"
          >
            <Minus className="w-5 h-5" />
          </button>

          <input
            type="number"
            value={value ?? ""}
            {...register(field.name, {
              valueAsNumber: true,
              min,
              max,
            })}
            disabled={field.disabled}
            className={`
              w-24 px-4 py-2 text-center text-lg
              border-2 rounded-xl
              bg-white dark:bg-gray-800
              text-gray-900 dark:text-gray-100
              focus:outline-none focus:ring-2 focus:ring-blue-500
              transition-all duration-200
              ${error 
                ? "border-red-500 dark:border-red-400 focus:ring-red-500" 
                : "border-gray-300 dark:border-gray-600 focus:border-blue-500"
              }
              ${field.disabled ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900" : ""}
            `}
            step={step}
            min={min}
            max={max}
          />

          <button
            type="button"
            onClick={inc}
            disabled={field.disabled || (Number(value) || 0) >= max}
            className={`
              w-12 h-12 rounded-xl border-2
              flex items-center justify-center
              transition-all duration-200
              ${field.disabled || (Number(value) || 0) >= max
                ? "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:scale-105 active:scale-95"
              }
            `}
            aria-label="Increase value"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Helper Text */}
        {field.helperText && !error && (
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {field.helperText}
          </p>
        )}

        {/* Error Message */}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 text-center">
            {String(error.message)}
          </p>
        )}
      </div>
    </MyBox>
  );
}